import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import PlanDocument from "@/components/PlanDocument";
import RecallReminderModal from "@/components/RecallReminderModal";
import { usePatients } from "@/components/PatientPicker";
import { PatientSearchSelect } from "@/components/PatientPicker";
import ClinicalBrainPanel from "@/components/ClinicalBrainPanel";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, Loader2, Save, Printer, UploadCloud, Sliders, FileText, Wand2, ScanLine, CalendarClock, ExternalLink, CheckCircle2,
} from "lucide-react";

const splitLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const DURATION_OPTS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 16];
const SESSION_OPTS = [1, 2, 3, 4, 5, 6, 8, 10, 12];
// Supervised visit frequency — weekly down to once every 16 weeks (~4 months)
const FREQ_OPTS = [
  "2x / week", "1x / week", "1x / 2 weeks", "1x / 3 weeks", "1x / 4 weeks",
  "1x / 6 weeks", "1x / 8 weeks", "1x / 12 weeks", "1x / 16 weeks",
];

export default function Builder() {
  const [params] = useSearchParams();
  const [sel, setSel] = useState(params.get("patient") || "");
  const [mode, setMode] = useState("rehabilitation");
  const [includeMoti, setIncludeMoti] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activePhase, setActivePhase] = useState("0");
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallPhase, setRecallPhase] = useState(null);
  const [rhmpBusy, setRhmpBusy] = useState(false);
  const [persisted, setPersisted] = useState(false);
  const [phaseUploads, setPhaseUploads] = useState({});
  const [uploadingPhase, setUploadingPhase] = useState(null);

  useEffect(() => {
    if (!plan?.id || !persisted) { return; }
    api.get(`/plans/${plan.id}/phase-uploads`).then((r) => setPhaseUploads(r.data || {})).catch(() => {});
  }, [plan?.id, persisted]);

  const uploadPhase = async (phase) => {
    let p = plan;
    if (!persisted) { p = await save("draft"); if (!p) return; }
    setUploadingPhase(phase);
    try {
      const { data } = await api.post(`/plans/${p.id}/phase-upload?phase=${phase}`);
      setPhaseUploads((u) => ({ ...u, [String(phase)]: data.uploaded_at }));
      toast.success(`Phase ${phase + 1} PDF saved to the patient's Files in Cliniko`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed — retry"); }
    finally { setUploadingPhase(null); }
  };

  useEffect(() => { if (params.get("patient")) setSel(params.get("patient")); }, [params]);

  const generate = async () => {
    if (!sel) { toast.error("Select a patient first"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/plans/generate", { cliniko_id: sel, mode, include_moti: includeMoti });
      setPlan(data);
      setPersisted(false);
      setActivePhase("0");
      toast.success("Draft plan generated — adjust phases, then save");
    } catch (e) { toast.error(e.response?.data?.detail || "Plan generation failed — retry"); }
    finally { setGenerating(false); }
  };

  const updatePhase = (i, patch) => {
    setPersisted(false);
    setPlan((pl) => {
      const phases = pl.phases.map((ph, k) => (k === i ? { ...ph, ...patch } : ph));
      const total = phases.reduce((a, ph) => a + Number(ph.duration_weeks || 0), 0);
      const sess = phases.reduce((a, ph) => a + Number(ph.sessions || 0), 0);
      return { ...pl, phases, overview: { ...pl.overview, total_duration_weeks: total, recommended_sessions: sess, phase_names: phases.map((x) => x.title) } };
    });
  };
  const updateExercise = (pi, ei, patch) => {
    setPersisted(false);
    setPlan((pl) => {
      const phases = pl.phases.map((ph, k) => (k !== pi ? ph : { ...ph, therapeutic_exercise: ph.therapeutic_exercise.map((ex, j) => (j === ei ? { ...ex, ...patch } : ex)) }));
      return { ...pl, phases };
    });
  };

  const save = async (status) => {
    setSaving(true);
    try {
      const payload = { ...plan, status: status || plan.status || "draft" };
      const { data } = await api.post("/plans", payload);
      setPlan((p) => ({ ...p, id: data.id, status: payload.status }));
      setPersisted(true);
      toast.success(status === "finalized" ? "Plan finalised & saved" : "Draft saved to Plan History");
      return data;
    } catch { toast.error("Save failed"); return null; }
    finally { setSaving(false); }
  };

  const pushCliniko = async () => {
    let p = plan;
    if (!persisted) { p = await save("draft"); if (!p) return; }
    try {
      const { data } = await api.post(`/plans/${p.id}/push-cliniko`);
      setPlan((x) => ({ ...x, status: "pushed" }));
      if (data.mode === "live" && !data.attached) toast.warning(data.message);
      else toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Push to Cliniko failed — retry"); }
  };

  const exportPdf = async (phase = null) => {
    let p = plan;
    if (!persisted) { p = await save("draft"); if (!p) return; }
    setExporting(true);
    try {
      const q = phase != null ? `?phase=${phase}` : "";
      const res = await api.get(`/plans/${p.id}/pdf${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const base = (plan.patient_name || "plan").replace(/\s+/g, "_");
      a.download = phase != null ? `${base}_phase${phase + 1}.pdf` : `${base}_plan.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(phase != null ? `Phase ${phase + 1} PDF downloaded` : "Full plan PDF downloaded");
    } catch { toast.error("Could not generate PDF — retry"); }
    finally { setExporting(false); }
  };

  const phaseStart = (idx) => {
    const base = plan?.created_at ? new Date(plan.created_at) : new Date();
    const weeksBefore = (plan?.phases || []).slice(0, idx).reduce((a, ph) => a + Number(ph.duration_weeks || 0), 0);
    const d = new Date(base.getTime() + weeksBefore * 7 * 86400000);
    try { return d.toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
  };

  const openRecall = async (phaseIndex = null) => {
    if (!persisted) { const d = await save("draft"); if (!d) return; }
    setRecallPhase(phaseIndex);
    setRecallOpen(true);
  };

  const syncRhmp = async () => {
    let p = plan;
    if (!persisted) { p = await save("draft"); if (!p) return; }
    setRhmpBusy(true);
    try {
      const res = await api.post(`/plans/${p.id}/rhmp-sync`);
      const { url, created, has_email } = res.data;
      window.open(url, "_blank", "noopener");
      if (!has_email) toast.warning("Synced to Rehab My Patient, but no email is on file — add one in RHMP to email the plan");
      else toast.success(`${created ? "Patient created in" : "Patient found in"} Rehab My Patient — build the plan and hit "Email to Patient" there`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sync with Rehab My Patient");
    } finally { setRhmpBusy(false); }
  };

  return (
    <Layout title="Plan Builder" subtitle="Notes + intake + Moti → Claude Sonnet 5 → editable 3-phase draft → branded PDF → Cliniko">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        {/* Controls / editor */}
        <div className="lg:col-span-7 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm no-print">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-slate-500">Patient</Label>
                <div className="mt-1.5"><PatientSearchSelect value={sel} onChange={setSel} testid="builder-patient-select" placeholder="Search patient by name or ID..." /></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Plan type</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="builder-mode-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="rehabilitation">Rehabilitation plan</SelectItem>
                    <SelectItem value="posture">Posture plan</SelectItem>
                    <SelectItem value="maintenance">Maintenance plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={includeMoti} onCheckedChange={setIncludeMoti} data-testid="builder-moti-switch" />
                <ScanLine className="h-4 w-4 text-indigo-500" /> Include Moti posture context
              </label>
              <Button onClick={generate} disabled={generating} data-testid="builder-generate-btn" className="bg-teal-600 hover:bg-teal-700">
                {generating ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Generating...</> : <><Sparkles className="mr-1.5 h-4 w-4" /> Generate draft</>}
              </Button>
            </div>
          </div>

          {generating && (
            <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm no-print">
              <Wand2 className="mx-auto h-8 w-8 animate-pulse text-teal-500" />
              <p className="mt-3 text-sm text-slate-500">Claude Sonnet 5 is drafting a locked-schema plan with verified citations...</p>
            </div>
          )}

          {plan && !generating && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm no-print">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800"><Sliders className="h-4 w-4 text-teal-600" /> Adjust phases <span className="text-xs font-normal text-slate-400">AI proposed — override anything</span></div>
              <Tabs value={activePhase} onValueChange={setActivePhase}>
                <TabsList className="bg-slate-100">
                  {plan.phases.map((ph, i) => <TabsTrigger key={ph._k || i} value={String(i)} data-testid={`treatment-phase-tab-${ph.number}`}>Phase {ph.number}</TabsTrigger>)}
                </TabsList>
                {plan.phases.map((ph, i) => (
                  <TabsContent key={ph._k || i} value={String(i)} className="mt-4 space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Input value={ph.title} onChange={(e) => updatePhase(i, { title: e.target.value })} data-testid={`phase-title-${i}`} className="mt-1" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Duration (weeks)</Label>
                        <Select value={ph.duration_weeks ? String(ph.duration_weeks) : ""} onValueChange={(v) => updatePhase(i, { duration_weeks: Number(v) })}>
                          <SelectTrigger data-testid={`phase-duration-${i}`} className="mt-1"><SelectValue placeholder="Weeks" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {DURATION_OPTS.map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "week" : "weeks"}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Sessions</Label>
                        <Select value={ph.sessions ? String(ph.sessions) : ""} onValueChange={(v) => updatePhase(i, { sessions: Number(v) })}>
                          <SelectTrigger data-testid={`phase-sessions-${i}`} className="mt-1"><SelectValue placeholder="Sessions" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {SESSION_OPTS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Frequency</Label>
                        <Select value={ph.supervised_frequency || ""} onValueChange={(v) => updatePhase(i, { supervised_frequency: v })}>
                          <SelectTrigger data-testid={`phase-frequency-${i}`} className="mt-1"><SelectValue placeholder="Frequency" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {(ph.supervised_frequency && !FREQ_OPTS.includes(ph.supervised_frequency) ? [ph.supervised_frequency, ...FREQ_OPTS] : FREQ_OPTS).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label className="text-xs text-slate-500">Summary</Label><Textarea value={ph.summary} onChange={(e) => updatePhase(i, { summary: e.target.value })} className="mt-1 text-sm" /></div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div><Label className="text-xs text-slate-500">Milestones (one per line)</Label><Textarea value={(ph.milestones || []).join("\n")} onChange={(e) => updatePhase(i, { milestones: splitLines(e.target.value) })} className="mt-1 min-h-[90px] text-sm" /></div>
                      <div><Label className="text-xs text-slate-500">Manual therapy (one per line)</Label><Textarea value={(ph.manual_therapy || []).join("\n")} onChange={(e) => updatePhase(i, { manual_therapy: splitLines(e.target.value) })} className="mt-1 min-h-[90px] text-sm" /></div>
                    </div>
                    <div><Label className="text-xs text-slate-500">Patient education (one per line)</Label><Textarea value={(ph.patient_education || []).join("\n")} onChange={(e) => updatePhase(i, { patient_education: splitLines(e.target.value) })} className="mt-1 text-sm" /></div>
                    <div>
                      <Label className="text-xs text-slate-500">Therapeutic exercises</Label>
                      <div className="mt-1 space-y-2">
                        {ph.therapeutic_exercise.map((ex, ei) => (
                          <div key={ex._k || ei} className="grid grid-cols-12 gap-1.5 rounded-lg bg-slate-50 p-2">
                            <Input value={ex.name} onChange={(e) => updateExercise(i, ei, { name: e.target.value })} className="col-span-5 h-8 text-xs" />
                            <Input value={ex.sets} onChange={(e) => updateExercise(i, ei, { sets: e.target.value })} className="col-span-2 h-8 text-xs" placeholder="sets" />
                            <Input value={ex.reps} onChange={(e) => updateExercise(i, ei, { reps: e.target.value })} className="col-span-2 h-8 text-xs" placeholder="reps" />
                            <Input value={ex.frequency} onChange={(e) => updateExercise(i, ei, { frequency: e.target.value })} className="col-span-3 h-8 text-xs" placeholder="freq" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                      <Button size="sm" variant="outline" onClick={() => exportPdf(i)} disabled={exporting} data-testid={`phase-export-pdf-${i}`}>
                        <Printer className="mr-1.5 h-3.5 w-3.5" /> Export Phase {ph.number} PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => uploadPhase(i)} disabled={uploadingPhase === i} data-testid={`phase-upload-cliniko-${i}`} className="border-teal-200 text-teal-700 hover:bg-teal-50">
                        {uploadingPhase === i ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="mr-1.5 h-3.5 w-3.5" />} Upload Phase {ph.number} to Cliniko
                      </Button>
                      {phaseUploads[String(i)] && (
                        <span className="inline-flex items-center gap-1 self-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700" data-testid={`phase-uploaded-badge-${i}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Uploaded {new Date(phaseUploads[String(i)]).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openRecall(i)} data-testid={`phase-recall-${i}`} className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                        <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Recall for this phase
                      </Button>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <div><Label className="mt-3 block text-xs text-slate-500">Overall summary</Label><Textarea value={plan.overall_summary} onChange={(e) => { setPersisted(false); setPlan({ ...plan, overall_summary: e.target.value }); }} className="mt-1 text-sm" /></div>
            </div>
          )}

          {sel && <div className="no-print"><ClinicalBrainPanel clinikoId={sel} /></div>}
        </div>

        {/* Live branded preview */}
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          {plan ? (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 no-print">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><FileText className="h-4 w-4 text-teal-600" /> Branded preview
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${{ pushed: "bg-emerald-100 text-emerald-700", finalized: "bg-blue-100 text-blue-700" }[plan.status] || "bg-slate-100 text-slate-500"}`}>{plan.status || "draft"}</span>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn"><Save className="mr-1 h-3.5 w-3.5" /> Save</Button>
                  <Button size="sm" variant="outline" onClick={() => exportPdf(null)} disabled={exporting} data-testid="treatment-plan-export-pdf-btn">{exporting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Printer className="mr-1 h-3.5 w-3.5" />} Full PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => openRecall(null)} data-testid="treatment-plan-recall-btn" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"><CalendarClock className="mr-1 h-3.5 w-3.5" /> Recall</Button>
                  <Button size="sm" variant="outline" onClick={syncRhmp} disabled={rhmpBusy} data-testid="plan-open-rhmp-btn" className="border-sky-200 text-sky-700 hover:bg-sky-50">{rhmpBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1 h-3.5 w-3.5" />} Open in RHMP</Button>
                  <Button size="sm" onClick={pushCliniko} data-testid="treatment-plan-push-cliniko-btn" className="bg-teal-600 hover:bg-teal-700"><UploadCloud className="mr-1 h-3.5 w-3.5" /> Push</Button>
                </div>
              </div>
              <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-5"><PlanDocument plan={plan} /></div>
            </div>
          ) : (
            <div className="grid h-80 place-items-center rounded-2xl border border-dashed border-slate-300 bg-white text-slate-400 no-print">
              <div className="text-center"><FileText className="mx-auto mb-2 h-8 w-8" /> Generate a plan to see the branded preview</div>
            </div>
          )}
        </div>
      </div>
      <RecallReminderModal open={recallOpen} onOpenChange={setRecallOpen} plan={plan}
        phase={recallPhase != null && plan?.phases?.[recallPhase] ? {
          index: recallPhase, number: plan.phases[recallPhase].number,
          title: plan.phases[recallPhase].title, startDate: phaseStart(recallPhase),
        } : null} />
    </Layout>
  );
}
